import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import DOMPurify from 'dompurify';
import { useIsDark } from '@/hooks/useIsDark';
import {
  Chart as ChartJS,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
} from 'chart.js';
import { Radar } from 'react-chartjs-2';
import { useUserContext } from '@/contexts/UserContext';
import { MOCK_ACTIONS } from '@/data/mockCatalog';
import {
  SAFE_DEMO_USER,
  SAFE_CELEBRATION_PHRASES,
  SAFE_GIVEN_SURVEYS,
  getAvgByFundamental,
  getOverallAvg,
  getScoresForUser,
  pbLevelIndex,
  safeConfidence,
} from '@/data/mockSafeData';
import { USE_MOCK } from '@/api/client';
import type { ActionItem } from '@/api/types';
import { useAnalyticsPersonal } from '@/hooks/useAnalytics';
import { usePersonalGrowth } from '@/hooks/usePersonalGrowth';
import { PageSpinner } from '@/components/common/PageSpinner';
import { useActions } from '@/hooks/useActions';
import { useDashboardStats } from '@/hooks/usePendingInstances';
import { useFundamentals, getAttitudeName, getAttitudeDescription } from '@/hooks/useFundamentals';
import { useTips } from '@/hooks/useTips';
import { FundamentalPopup } from './components/FundamentalPopup';
import { HourglassSvg } from './components/HourglassSvg';
import { JourneyChart } from './components/JourneyChart';
import { KudosCarousel } from './components/KudosCarousel';
import { buildNarrative } from './utils/buildNarrative';
import './SafeView.css';

ChartJS.register(RadialLinearScale, PointElement, LineElement, Filler, Tooltip, Legend);

// Derived from the current date — not hardcoded
const now           = new Date();
const CURRENT_MONTH = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
const pastDate      = new Date(now.getFullYear(), now.getMonth() - 3, 1);
const PAST_MONTH    = `${pastDate.getFullYear()}-${String(pastDate.getMonth() + 1).padStart(2, '0')}`;

// Named thresholds for score quality bands
const SCORE_HIGH_THRESHOLD = 3.2;
const SCORE_LOW_THRESHOLD  = 2.8;

const BAR_COLORS = ['#342F82', '#20B15A', '#F08521', '#E11F26'];

// ── Main component ─────────────────────────────────────────────────────────
export function SafeView() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language.split('-')[0];
  const { demoUser } = useUserContext();
  const [popupFundId, setPopupFundId] = useState<string | null>(null);
  const [ctaCommitted, setCtaCommitted] = useState(false);
  const userId = SAFE_DEMO_USER.id;

  // ── API hooks (only active in real mode; mock mode uses local helpers) ──────
  const { data: personalData, isLoading: loadingPersonal } = useAnalyticsPersonal(demoUser.userId);
  const { data: growthData, isLoading: loadingGrowth }     = usePersonalGrowth(demoUser.userId);
  const { data: actionsData = [] } = useActions();
  const { data: dashStats }    = useDashboardStats();
  const { data: catalogData = [], isLoading: catalogLoading } = useFundamentals();
  const { data: tipsData = [] }    = useTips();

  // ── Derived score data ────────────────────────────────────────────────────
  type FundAvg = { id: string; avg: number; count: number };

  const fundAvgs: FundAvg[] = USE_MOCK
    ? getAvgByFundamental(userId, CURRENT_MONTH)
    : (personalData?.fundamentalScores ?? []).map(f => ({
        id: f.id, avg: f.score, count: f.evaluations,
      }));

  const totalEvals: number = USE_MOCK
    ? getScoresForUser(userId, CURRENT_MONTH).length
    : (personalData?.fundamentalScores ?? []).reduce((s, f) => s + f.evaluations, 0);

  const currentAvg: number = USE_MOCK
    ? getOverallAvg(userId, CURRENT_MONTH)
    : (personalData?.avgScore ?? 0);

  const pastEntry  = growthData?.history?.find(h => h.month === PAST_MONTH);
  const pastAvg: number = USE_MOCK
    ? getOverallAvg(userId, PAST_MONTH)
    : (pastEntry?.overallAverage ?? 0);

  const pastFundAvgs: FundAvg[] = USE_MOCK
    ? getAvgByFundamental(userId, PAST_MONTH)
    : (pastEntry?.fundamentals ?? []).map(f => ({
        id: f.fundamentalId, avg: f.average, count: 0,
      }));

  // ── Progress bar (from dashboard stats in real mode) ─────────────────────
  const progressPct   = USE_MOCK ? 60 : Math.round((dashStats?.participationRate ?? 0.5) * 100);
  const answeredCount = USE_MOCK ? 3 : (dashStats?.totalAnswered ?? 0);
  const totalCount    = USE_MOCK ? 5 : ((dashStats?.totalAnswered ?? 0) + (dashStats?.totalOmitted ?? 0));

  // ── Actions (daily challenge + growth cards) ──────────────────────────────
  const allActions = USE_MOCK
    ? MOCK_ACTIONS
    : (actionsData as unknown as typeof MOCK_ACTIONS);

  const levelLabels = [t('safe.level0'), t('safe.level1'), t('safe.level2'), t('safe.level3')] as const;

  const sorted    = [...fundAvgs].sort((a, b) => b.avg - a.avg);
  const strengths = sorted.filter(f => f.avg >= SCORE_HIGH_THRESHOLD).slice(0, 3);
  const growth    = sorted.filter(f => f.avg < SCORE_LOW_THRESHOLD).slice(0, 2);

  const narrative = buildNarrative(strengths, growth, currentAvg, pastAvg, t as (key: string, opts?: Record<string, unknown>) => string, levelLabels, catalogData, lang);

  const challengeKey = growth.length > 0 ? growth[0].id : (sorted[sorted.length - 1]?.id ?? 'learner');
  const challengeAvg = fundAvgs.find(f => f.id === challengeKey)?.avg ?? 1;
  const action = allActions.find(a =>
    a.fundamentalId === challengeKey && a.active &&
    challengeAvg >= a.threshold && challengeAvg <= a.thresholdMax,
  ) ?? allActions.find(a => a.fundamentalId === challengeKey && a.active);
  const challengeFund = catalogData.find(f => f.id === challengeKey);
  const challengeIcon = challengeFund?.icon ?? 'fa-bolt';

  const diff = currentAvg > 0 && pastAvg > 0 ? Math.round((currentAvg - pastAvg) * 10) / 10 : 0;
  const trendIcon  = diff > 0 ? 'fa-arrow-trend-up' : diff < 0 ? 'fa-arrow-trend-down' : 'fa-minus';
  const trendClass = diff > 0 ? 'green' : diff < 0 ? 'orange' : 'muted';
  const trendText  = diff > 0 ? t('safe.trend.up') : diff < 0 ? t('safe.trend.down') : t('safe.trend.stable');

  const topFund = strengths[0] ?? sorted[0];

  // Radar chart data — ordered by the real catalog (garantizado cargado por el guard de abajo)
  const radarCatalog = [...catalogData].sort((a, b) => a.order - b.order);

  const radarLabels = radarCatalog.map(f => f.shortNames?.[lang] ?? f.shortName ?? f.names?.[lang] ?? f.id);
  const currentData = radarCatalog.map(f => fundAvgs.find(x => x.id === f.id)?.avg ?? 0);
  const pastData = radarCatalog.map(f => pastFundAvgs.find(x => x.id === f.id)?.avg ?? 0);

  const isDark = useIsDark();
  const gridColor   = isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.05)';
  const labelColor  = isDark ? '#94a3b8' : '#9CA3AF';
  const pointBorder = isDark ? 'rgba(30,18,68,0.9)' : '#fff';

  const radarData = {
    labels: radarLabels,
    datasets: [
      {
        label: t('safe.today'),
        data: currentData,
        borderColor: '#F472B6',
        backgroundColor: 'rgba(244,114,182,0.25)',
        pointBackgroundColor: '#F472B6',
        pointBorderColor: pointBorder,
        pointBorderWidth: 2,
        borderWidth: 2.5,
      },
      {
        label: t('safe.threeMonthsAgo'),
        data: pastData,
        borderColor: '#38BDF8',
        backgroundColor: 'rgba(56,189,248,0.15)',
        pointBackgroundColor: '#38BDF8',
        pointBorderColor: pointBorder,
        pointBorderWidth: 2,
        borderWidth: 2,
      },
    ],
  };

  const radarOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom' as const,
        labels: {
          usePointStyle: true,
          padding: 16,
          color: labelColor,
          font: { family: 'Montserrat,sans-serif', size: 11 },
        },
      },
      tooltip: {
        backgroundColor: '#1a1a2e',
        padding: 10,
        cornerRadius: 10,
      },
    },
    scales: {
      r: {
        min: 0, max: 4,
        ticks: { stepSize: 1, display: false },
        grid: { color: gridColor },
        angleLines: { color: gridColor },
        pointLabels: {
          font: { size: 10, family: 'Montserrat,sans-serif' },
          color: labelColor,
        },
      },
    },
  };

  const topFundObj = catalogData.find(f => f.id === topFund?.id);
  const celebPhrase = topFund ? (SAFE_CELEBRATION_PHRASES[topFund.id] ?? '') : '';

  const pastTopFund = pastFundAvgs.find(f => f.id === topFund?.id);
  const delta       = topFund && pastTopFund ? (topFund.avg - pastTopFund.avg).toFixed(1) : null;
  const deltaStr    = delta !== null ? (parseFloat(delta) >= 0 ? `+${delta}` : delta) : '';

  // Growth fund cards (4 fundamentals sorted by lowest avg)
  const growthFunds = [...sorted].sort((a, b) => a.avg - b.avg).slice(0, 4);

  const firstName = (demoUser.nombre || SAFE_DEMO_USER.name).split(' ')[0];
  const hour = new Date().getHours();
  const greeting = hour < 12 ? t('survey.greetingMorning') : hour < 18 ? t('survey.greetingAfternoon') : t('survey.greetingEvening');

  // ── Loading state — el catálogo siempre debe resolver desde la API/mock
  //    hook antes de renderizar (nunca caer a un catálogo hardcodeado) ──────
  if (catalogLoading || (!USE_MOCK && (loadingPersonal || loadingGrowth))) {
    return (
      <div className="ud-wrap">
        <PageSpinner minHeight="120px" />
      </div>
    );
  }

  // ── Empty state (< 3 evals) — catalogData ya está garantizado por el
  //    guard de carga de arriba, así que aquí nunca hace falta un spinner. ─
  if (totalEvals < 3) {
    return (
      <div className="ud-wrap">
        <div className="ud-empty-state">
          <div className="ud-empty-icon"><HourglassSvg /></div>
          <h2>{t('safe.empty.title')}</h2>
          <p>{t('safe.empty.desc')}</p>
          <div className="ud-empty-hint">
            <i className="fas fa-info-circle" />
            <span>{t('safe.empty.hint')}</span>
          </div>
          <div className="ud-empty-fundamentals">
            {[...catalogData].sort((a, b) => a.order - b.order).map(f => {
              const color = f.color ?? '#342F82';
              const name  = f.shortNames?.[lang] ?? f.shortName ?? f.names?.[lang] ?? f.id;
              const desc  = f.descriptions?.[lang] ?? f.descriptions?.['es'] ?? f.descriptions?.['en'] ?? f.description ?? '';
              const fundIcon = f.icon
                ? f.icon.startsWith('fa-')
                  ? <i className={`fas ${f.icon}`} />
                  : <img src={f.icon} alt="" style={{ width: 28, height: 28, objectFit: 'contain' }} />
                : <i className="fas fa-circle" />;
              const activeAttitudes = f.attitudes
                .filter(a => a.isActive)
                .sort((a, b) => a.order - b.order);

              if (activeAttitudes.length === 0) {
                return (
                  <div key={f.id} className="ud-empty-fund-card ud-empty-fund-card--compact">
                    <div className="ud-empty-fund-icon" style={{ background: `${color}18`, color }}>
                      {fundIcon}
                    </div>
                    <div className="ud-empty-fund-name">{name}</div>
                    {desc && <div className="ud-empty-fund-desc">{desc}</div>}
                  </div>
                );
              }

              return (
                <div key={f.id} className="ud-empty-fund-card ud-empty-fund-card--expanded" style={{ borderTopColor: color }}>
                  <div className="ud-empty-fund-header">
                    <div className="ud-empty-fund-icon" style={{ background: `${color}18`, color }}>
                      {fundIcon}
                    </div>
                    <div className="ud-empty-fund-name">{name}</div>
                  </div>
                  <div className="ud-empty-att-list">
                    {activeAttitudes.map(a => {
                      const attName = getAttitudeName(a, lang);
                      const attDesc = getAttitudeDescription(a, lang);
                      return (
                        <div key={a.id} className="ud-empty-att-item">
                          <div className="ud-empty-att-icon" style={{ background: `${color}18`, color }}>
                            {a.image
                              ? <img src={a.image} alt="" style={{ width: 20, height: 20, objectFit: 'contain' }} />
                              : <i className="fas fa-comment-dots" />
                            }
                          </div>
                          <div className="ud-empty-att-text">
                            <div className="ud-empty-att-name">{attName}</div>
                            {attDesc && <div className="ud-empty-att-desc">{attDesc}</div>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="ud-wrap">

      {/* ── HEADER ── */}
      <div className="ud-header-wrap">
        <div className="ud-persona-wave" aria-hidden="true">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 378.88 381.37" style={{ width: '100%', height: 'auto' }}>
            <g>
              <path fill="#332f81" d="M158.67,143.98c36.98,0,66.95-29.97,66.95-66.95S195.65,10.08,158.67,10.08s-66.95,29.97-66.95,66.95,29.97,66.95,66.95,66.95Z"/>
              <g>
                <ellipse fill="#fff" cx="143.89" cy="71.29" rx="7.92" ry="7.92">
                  <animate attributeName="ry" values="7.92;7.92;0.5;7.92;7.92;0;0;0;0;7.92;7.92" keyTimes="0;0.15;0.17;0.19;0.35;0.38;0.55;0.65;0.68;0.71;1" dur="3s" repeatCount="indefinite"/>
                </ellipse>
                <ellipse fill="#fff" cx="173.43" cy="71.29" rx="7.92" ry="7.92">
                  <animate attributeName="ry" values="7.92;7.92;0.5;7.92;7.92;0;0;0;0;7.92;7.92" keyTimes="0;0.15;0.17;0.19;0.35;0.38;0.55;0.65;0.68;0.71;1" dur="3s" repeatCount="indefinite"/>
                </ellipse>
              </g>
              <path fill="#ee5294" d="M160.27,152.99c25.15.79,45.27,21.42,45.27,46.73,0,19.37-11.79,36-28.59,43.1-5.31,2.28-11.13,3.58-17.24,3.71,31.24.5,56.51,26.07,56.51,57.42l44.97-110.85c-26.58-24.59-62.02-39.73-100.92-40.11Z"/>
              <path fill="#ef8421" d="M121.42,227.88c-5.91-7.83-9.41-17.58-9.41-28.14,0-25.82,20.95-46.77,46.77-46.77.5,0,.99.01,1.49.02-.5-.02-1-.04-1.5-.04-83.26,0-150.98,67.72-150.98,150.98,0,23.38,17.23,43.15,40.38,46.35l110.95,15.33c2.17.3,4.35.45,6.47.45h0c22.97-.01,43.02-16.97,46.27-40.39,3.55-25.6-14.33-49.19-39.93-52.74l-55.56-7.69c10.22-11.19,24.79-18.32,41.01-18.71-14.72-.4-27.73-7.63-35.96-18.65Z"/>
            </g>
          </svg>
        </div>
        <div className="ud-header">
          <div className="ud-header-left">
            <div className="ud-avatar">
              {(demoUser.nombre || SAFE_DEMO_USER.name)
                .split(' ').slice(0, 2).map(p => p[0]?.toUpperCase() ?? '').join('')}
            </div>
            <div className="ud-header-text">
              <div className="ud-header-section-name">{t('safe.sectionName')}</div>
              <h1>{greeting}, {firstName}</h1>
              <div className="ud-progress-wrap">
                <div className="ud-progress-meta">
                  <span>{t('safe.surveysAnswered')}</span>
                  <span>{answeredCount} / {totalCount || 5}</span>
                </div>
                <div className="ud-progress-bar">
                  <div className="ud-progress-fill" style={{ width: `${progressPct}%` }}>
                    <span>{progressPct}%</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="ud-header-right" aria-hidden="true">
            <div className="escena">
              <div className="nube tipo1 nube1" />
              <div className="nube tipo2 nube2" />
              <div className="nube tipo1 nube3" />
              <div className="nube tipo2 nube4" />
              <div className="personaje">
                <div className="ojo izq" />
                <div className="ojo der" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── ACT 01 ── */}
      <div className="ud-act-header">
        <span className="ud-act-num">01</span>
        <span className="ud-act-label">{t('safe.act01')}</span>
      </div>

      {/* Daily challenge */}
      <div className="ud-daily-challenge">
        <div className="ud-dc-left">
          <div className="ud-dc-mascot">
            <img src="/assets/persona-globo.svg" alt="" className="ud-dc-mascot-img" />
          </div>
        </div>
        <div className="ud-dc-right">
          {/* Profile narrative section */}
          <div className="ud-dc-profile-section">
            <div className="ud-dc-profile-header">
              <div className="ud-icon-box purple" style={{ width: 28, height: 28, borderRadius: 8, fontSize: '0.75rem' }}>
                <i className="fas fa-compass" />
              </div>
              <div>
                <h2 className="ud-dc-profile-title">{t('safe.profileTitle')}</h2>
                <small>{t('safe.profileSub')}</small>
              </div>
              <div className={`ud-dc-trend ${trendClass}`}>
                <i className={`fas ${trendIcon}`} /> {trendText}
              </div>
            </div>
            <div className="ud-dc-narrative" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(narrative) }} />
            <div className="ud-dc-evidence">
              <i className="fas fa-shield-alt" />
              {' '}{t('safe.basedOn', { count: totalEvals })}
            </div>
          </div>

          <h2 className="ud-dc-title">"{(action?.translations?.[lang]?.title ?? action?.title) ?? t('safe.defaultChallenge')}"</h2>
          <p className="ud-dc-desc">{(action?.translations?.[lang]?.description ?? action?.description) ?? t('safe.defaultChallengeDesc')}</p>
          {action?.nextLevelText && (
            <div className="ud-dc-nextlevel">
              <i className="fas fa-arrow-up-right-dots" />
              {action.translations?.[lang]?.nextLevelText ?? action.nextLevelText}
            </div>
          )}
          <div className="ud-dc-reinforces">
            <span className="ud-dc-reinforces-label">{t('safe.reinforces')}</span>
            <span className="ud-dc-fund-tag">
              <i className={`fas ${challengeIcon}`} />
              {challengeFund?.shortNames?.[lang] ?? challengeFund?.shortName ?? challengeFund?.names?.[lang] ?? challengeKey}
            </span>
          </div>

          <button
            type="button"
            className={`ud-dc-cta${ctaCommitted ? ' committed' : ''}`}
            onClick={() => setCtaCommitted(true)}
            disabled={ctaCommitted}
          >
            <span className="ud-dc-cta-icon"><i className={ctaCommitted ? 'fas fa-check-circle' : 'fas fa-rocket'} /></span>
            <span className="ud-dc-cta-text">
              {ctaCommitted ? t('safe.committed') : t('safe.tryToday')}
            </span>
            {!ctaCommitted && <span className="ud-dc-cta-arrow"><i className="fas fa-arrow-right" /></span>}
          </button>
        </div>
      </div>

      {/* Kudos carousel */}
      {strengths.length > 0 && (
        <KudosCarousel
          strengths={strengths}
          t={t}
          catalogData={catalogData}
          actionsData={actionsData as ActionItem[]}
          tipsData={tipsData}
        />
      )}

      {/* ── ACT 02 ── */}
      <div className="ud-act-header">
        <span className="ud-act-num">02</span>
        <span className="ud-act-label">{t('safe.act02')}</span>
      </div>

      {/* Charts row */}
      <div className="ud-chart-row">
        <div className="ud-card">
          <div className="ud-section-head">
            <div className="ud-icon-box cyan"><i className="fas fa-route" /></div>
            <div>
              <h3>{t('safe.evolution')}</h3>
              <small>{t('safe.evolutionSub')}</small>
            </div>
          </div>
          <JourneyChart />
        </div>
        <div className="ud-card">
          <div className="ud-section-head">
            <div className="ud-icon-box pink"><i className="fas fa-fingerprint" /></div>
            <div>
              <h3>{t('safe.radarTitle')}</h3>
              <small>{t('safe.radarSub')}</small>
            </div>
          </div>
          <div className="ud-radar-chart">
            <Radar data={radarData} options={radarOptions} />
          </div>
        </div>
      </div>

      {/* ── ACT 03 ── */}
      <div className="ud-act-header">
        <span className="ud-act-num">03</span>
        <span className="ud-act-label">{t('safe.act03')}</span>
      </div>

      {/* Fundamentals bar chart */}
      <div className="ud-card ud-fundamentals-card">
        <div className="ud-section-head">
          <div className="ud-icon-box cyan"><i className="fas fa-layer-group" /></div>
          <div>
            <h3>{t('safe.fundamentalsChart')}</h3>
            <small>{t('safe.fundamentalsChartSub')}</small>
          </div>
        </div>
        <div className="fp-bar-chart-scroll">
          <div className="fp-bar-chart">
            {sorted.map((f, i) => {
              const fund   = catalogData.find(x => x.id === f.id);
              const fName  = fund?.shortNames?.[lang] ?? fund?.shortName ?? fund?.names?.[lang] ?? f.id;
              const lvl    = pbLevelIndex(f.avg);
              const conf   = safeConfidence(f.count);
              const barPct = Math.round((f.avg / 4) * 100);
              const color  = fund?.color ?? BAR_COLORS[i % BAR_COLORS.length];
              const lvlLabel = t(`safe.level${lvl}`);
              return (
                <div
                  key={f.id}
                  className="fp-bar-col"
                  onClick={() => setPopupFundId(f.id)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={e => { if (e.key === 'Enter') setPopupFundId(f.id); }}
                  aria-label={fName}
                >
                  <span className="fp-bar-score">{f.avg}</span>
                  <div className="fp-bar-track">
                    <div className="fp-bar-fill" style={{ height: `${barPct}%`, background: color }} />
                  </div>
                  <div className="fp-bar-icon" style={{ background: `${color}18`, color }}>
                    {fund?.icon
                      ? fund.icon.startsWith('fa-')
                        ? <i className={`fas ${fund.icon}`} />
                        : <img src={fund.icon} alt="" className="fp-bar-icon-img" />
                      : <i className="fas fa-circle" />
                    }
                  </div>
                  <span className="fp-bar-label">{fName}</span>
                  <div className="fp-bar-tooltip">
                    <strong>{fName}</strong>
                    <span className="fp-bar-tooltip-level" style={{ color }}>{lvlLabel}</span>
                    <span className="fp-bar-tooltip-meta">{f.count} {t('safe.peers')} · {conf}</span>
                    <span className="fp-bar-tooltip-cta">
                      <i className="fas fa-expand-alt" /> {t('safe.clickDetail')}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Celebration + Growth */}
      {topFund && topFundObj && (
        <div className="fg-layout">
          {/* Left: Celebration */}
          <div className="fg-left">
            <div className="fg-celebration">
              <div className="fg-celebration-scene">
                <div className="fg-confetti">
                  <span>🎉</span><span>⭐</span><span>✨</span><span>🏆</span><span>🎊</span>
                </div>
                <img
                  src="/assets/persona-torso.svg"
                  alt=""
                  className="fg-celebration-mascot"
                  onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
                />
              </div>
              {deltaStr && (
                <div
                  className="fg-celebration-delta"
                  style={{ color: parseFloat(delta ?? '0') >= 0 ? 'var(--cp-green)' : 'var(--cp-orange)' }}
                >
                  <i className="fas fa-arrow-trend-up" /> {deltaStr}
                </div>
              )}
              <div className="fg-celebration-body">
                <div className="fg-celebration-score">
                  <span className="fg-score-big" style={{ color: topFundObj.color }}>{topFund.avg}</span>
                  <span className="fg-score-max">/4</span>
                </div>
                <div className="fg-celebration-fund">
                  <span
                    className="fg-fund-badge"
                    style={{ background: `${topFundObj.color}18`, color: topFundObj.color }}
                  >
                    🏅 {(topFundObj.shortNames?.[lang] ?? topFundObj.shortName ?? topFundObj.names?.[lang] ?? topFund.id).toUpperCase()}
                  </span>
                </div>
                <p className="fg-celebration-phrase">"{celebPhrase}"</p>
              </div>
            </div>
          </div>

          {/* Right: Growth cards */}
          <div className="fg-right">
            <div className="fg-right-header">
              <span className="fg-right-title">{t('safe.growthTitle')}</span>
              <small>{t('safe.growthSub')}</small>
            </div>
            <div className="fg-cards-list">
              {growthFunds.map(f => {
                const gFund = catalogData.find(x => x.id === f.id);
                const gLvl = pbLevelIndex(f.avg);
                const gColor = gFund?.color ?? '#342F82';
                const gName  = gFund?.shortNames?.[lang] ?? gFund?.shortName ?? gFund?.names?.[lang] ?? f.id;
                const gLvlLabel = t(`safe.level${gLvl}`);
                const gAction = allActions.find(a =>
                  a.fundamentalId === f.id && a.active &&
                  f.avg >= a.threshold && f.avg <= a.thresholdMax,
                ) ?? allActions.find(a => a.fundamentalId === f.id && a.active);
                const lvlColors = ['#E11F26', '#F08521', '#40B8E9', '#20B15A'];

                const steps = [0, 1, 2, 3].map(i => (
                  <div
                    key={i}
                    className="fg-step"
                    style={i <= gLvl ? { background: lvlColors[gLvl] } : {}}
                  />
                ));

                return (
                  <div key={f.id} className="fg-card">
                    <div className="fg-card-top">
                      <div
                        className="fg-card-icon"
                        style={{ background: `${gColor}18`, color: gColor }}
                      >
                        {gFund?.icon
                          ? gFund.icon.startsWith('fa-')
                            ? <i className={`fas ${gFund.icon}`} />
                            : <img src={gFund.icon} alt="" style={{ width: 34, height: 34, objectFit: 'contain' }} />
                          : <i className="fas fa-circle" />
                        }
                      </div>
                      <div className="fg-card-info">
                        <h4>{gName}</h4>
                        <span className="fg-card-level" style={{ color: gColor }}>{gLvlLabel}</span>
                      </div>
                      <span className="fg-card-score" style={{ color: gColor }}>
                        {f.avg}<small>/4</small>
                      </span>
                    </div>
                    <div className="fg-stepper">{steps}</div>
                    {gAction && (
                      <div className="fg-card-action">
                        <i className="fas fa-bolt" />
                        <span>{gAction.translations?.[lang]?.title ?? gAction.title}</span>
                      </div>
                    )}
                    {gAction?.nextLevelText && (
                      <div className="fg-card-nextlevel">
                        <i className="fas fa-arrow-up-right-dots" />
                        {gAction.translations?.[lang]?.nextLevelText ?? gAction.nextLevelText}
                      </div>
                    )}
                    <span className="fg-card-meta">
                      <i className="fas fa-eye" /> {f.count} {t('safe.feedbacks')}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Given feedback card */}
      <div className="ud-card">
        <div className="ud-section-head">
          <div className="ud-icon-box orange"><i className="fas fa-paper-plane" /></div>
          <div>
            <h3>{t('safe.givenTitle')}</h3>
            <small>{t('safe.givenSub')}</small>
          </div>
        </div>
        <div className="ud-given-list">
          {SAFE_GIVEN_SURVEYS.map(s => (
            <div key={s.name} className="ud-given-row">
              <div className="ud-given-dot" />
              <div className="ud-given-info">
                <span className="ud-given-name">{s.name}</span>
                <span className="ud-given-meta">{s.people} {s.people !== 1 ? t('safe.personsPlural') : t('safe.personSingular')} · {s.date}</span>
              </div>
              <span className="ud-given-status">
                <i className="fas fa-check-circle" /> {s.status}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Footer */}
      <div className="ud-footer">
        <p><i className="fas fa-lock" /> {t('safe.footerPrivacy')}</p>
        <p><i className="fas fa-heart" /> {t('safe.footerHeart')}</p>
      </div>

      {/* Popup */}
      {popupFundId && (
        <FundamentalPopup
          fundamentalId={popupFundId}
          onClose={() => setPopupFundId(null)}
          catalogData={catalogData}
          personalData={personalData}
          growthData={growthData}
        />
      )}
    </div>
  );
}
